import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const EnterpriseAuthContext = createContext();

export const useEnterpriseAuth = () => {
  const context = useContext(EnterpriseAuthContext);
  if (!context) {
    throw new Error('useEnterpriseAuth must be used within EnterpriseAuthProvider');
  }
  return context;
};

export const EnterpriseAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('auth_token'));

  useEffect(() => {
    // Check for existing auth token
    const existingToken = localStorage.getItem('auth_token');
    if (existingToken && existingToken.startsWith('mock-jwt-token')) {
      // Mock user session restore
      setUser({
        id: 'admin-user-id',
        email: 'admin@nexorasim.onmicrosoft.com',
        full_name: 'NexoraSIM Administrator',
        role: 'enterprise_admin',
        permissions: ['create_profile', 'view_billing', 'view_audit_logs']
      });
      setOrganization({
        id: 'nexorasim-org',
        name: 'NexoraSIM Enterprise',
        domain: 'nexorasim.onmicrosoft.com'
      });
      setToken(existingToken);
    }
    setLoading(false);
  }, []);

  const fetchUserProfile = async (authToken) => {
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data.user);
      setOrganization(response.data.organization);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      await signOut();
    }
  };

  const signInFirebase = async (email, password) => {
    try {
      // Mock authentication for demo purposes
      if (email === 'admin@nexorasim.onmicrosoft.com' && password === 'Melilite7%') {
        const mockUser = {
          uid: 'mock-admin-uid',
          email: email,
          displayName: 'NexoraSIM Admin'
        };
        const mockToken = 'mock-jwt-token-' + Date.now();
        
        setToken(mockToken);
        localStorage.setItem('auth_token', mockToken);
        
        // Mock user profile
        setUser({
          id: 'admin-user-id',
          email: email,
          full_name: 'NexoraSIM Administrator',
          role: 'enterprise_admin',
          permissions: ['create_profile', 'view_billing', 'view_audit_logs']
        });
        setOrganization({
          id: 'nexorasim-org',
          name: 'NexoraSIM Enterprise',
          domain: 'nexorasim.onmicrosoft.com'
        });
        
        return { success: true };
      } else {
        return { success: false, error: 'Invalid credentials' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signInMicrosoft = async () => {
    try {
      const { user: msUser, token: accessToken } = await signInWithMicrosoft();
      setToken(accessToken);
      localStorage.setItem('auth_token', accessToken);
      await fetchUserProfile(accessToken);
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signOut = async () => {
    try {
      await signOutUser();
      await signOutMicrosoft();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setUser(null);
      setOrganization(null);
      setToken(null);
      localStorage.removeItem('auth_token');
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return user.permissions?.includes(permission) || false;
  };

  const value = {
    user,
    organization,
    token,
    loading,
    signInFirebase,
    signInMicrosoft,
    signOut,
    hasPermission
  };

  return (
    <EnterpriseAuthContext.Provider value={value}>
      {children}
    </EnterpriseAuthContext.Provider>
  );
};